import { Component, Inject, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MessageService } from "primeng/api";
import { debounceTime, distinctUntilChanged } from "rxjs";
import { UpdateBankAccountBody } from "src/models/investor";
import { InvestorService } from "src/services/investor.service";

@Component({
  selector: "app-update-bank-details-modal",
  templateUrl: "./update-bank-details-modal.component.html",
  styleUrls: ["./update-bank-details-modal.component.scss"],
  providers: [MessageService],
})
export class UpdateBankDetailsModalComponent implements OnInit {
  bankForm: FormGroup;
  loading: boolean = false;
  isIfscValid: boolean = true;

  constructor(
    private fb: FormBuilder,
    private investorService: InvestorService,
    private messageService: MessageService,
    public dialogRef: MatDialogRef<UpdateBankDetailsModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { bank: any; user_id: number }
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.setupIFSCListener();
  }

  initForm(): void {
    this.bankForm = this.fb.group({
      account_number: [
        this.data.bank?.account_number || "",
        [Validators.required],
      ],
      holder_name: [this.data.bank?.holder_name || "", [Validators.required]],
      ifsc: [
        this.data.bank?.ifsc || "",
        [Validators.required, Validators.pattern("[A-Z]{4}0[A-Z0-9]{6}")],
      ],
      bank_name: [this.data.bank?.bank_name || "", [Validators.required]],
    });
  }

  setupIFSCListener(): void {
    this.bankForm
      .get("ifsc")
      ?.valueChanges.pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((ifsc) => {
        if (ifsc && this.bankForm.get("ifsc")?.valid) {
          this.fetchBankName(ifsc);
        } else {
          this.isIfscValid = this.bankForm.get("ifsc")?.valid || false;
        }
      });
  }

  fetchBankName(ifsc: string): void {
    this.loading = true;
    this.investorService.fetchBankName(ifsc).subscribe({
      next: (res) => {
        this.bankForm.patchValue({
          bank_name: res.data.bank_name,
        });
        this.isIfscValid = true;
        this.loading = false;
      },
      error: (err) => {
        this.messageService.add({
          severity: "error",
          summary: "Error",
          detail: "Invalid IFSC code or unable to fetch bank name",
        });
        this.isIfscValid = false;
        this.loading = false;
      },
    });
  }

  onSubmit(): void {
    if (this.bankForm.invalid || !this.isIfscValid) {
      this.bankForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    const bankData: UpdateBankAccountBody = {
      user_id: this.data.user_id,
      account_number: this.bankForm.value.account_number,
      holder_name: this.bankForm.value.holder_name,
      ifsc: this.bankForm.value.ifsc,
      bank_name: this.bankForm.value.bank_name,
    };

    this.investorService.updateBankAccount(bankData).subscribe({
      next: (res) => {
        // Check if the response indicates a failure (message_code: 0)
        if (res.message_code === 0) {
          // Handle as an error even though it's a 200 response
          this.messageService.add({
            severity: "error",
            summary: "Verification Failed",
            detail: res.message || "Bank Account verification failed",
          });
          this.loading = false;
        } else {
          // Handle as success
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: res.message || "Bank details updated successfully",
          });
          this.loading = false;
          this.dialogRef.close(true);
        }
      },
      error: (err) => {
        this.messageService.add({
          severity: "error",
          summary: "Error",
          detail: err.error?.message || "Failed to update bank details",
        });
        this.loading = false;
      },
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
